import CustomHTML from 'components/CustomHTML'
import { useCallback, useContext, useEffect, useState } from 'react'
import { Button, Image } from 'react-bootstrap'
import DrawingQuestion, { AssessmentDrawingQuestion, DrawingQuestionType, isAssessmentQuestion, isLessonQuestion, isTopicQuestion, LessonDrawingQuestion, TopicDrawingQuestion } from './DrawingQuestion'
import FullScreenWrapper from './FullScreenWrapper'
import styles from "./DrawingQuestion.module.scss"
import useBoolean from 'helpers/useBoolean'
import { AssignmentTopicContext } from '../AssignmentTopicContext'
import { LessonContext } from 'components/Lessons/LessonContext'
import { DrawingAnswer } from 'shared/types/studentTypes'
import { getSubmissionDrawingQuestions } from 'shared/routes/assignments'
import DrawingQuestionImport from './DrawingQuestionImport'
import { AssignmentContext } from '../AssignmentContext'
import ToolTip from "components/General/ToolTip"
import { LessonFeedbackContext } from "../contexts/LessonFeedbackContext"
import TeacherFeedback from "./TeacherFeedback"
import { BeatLoader } from "react-spinners"

interface Props {
    questionType: DrawingQuestionType
    question: TopicDrawingQuestion | LessonDrawingQuestion | AssessmentDrawingQuestion
    
    // lesson drawing question props
    saveLesson?: ({ force }?: { force?: boolean }) => void
    autoSave?: () => void
    stopDraggable?: () => void

    // assessment drawing question props
    initialAssessmentDrawingData?: DrawingAnswer
    updateDrawingResponses?: (props: { questionNumber: number, answer: string, base64String: string, updateInitialState?: boolean }) => void

    // topic drawing question props
    handleAnswerChange?: (sectionNum: number, questionsNum: number, answer: string, drawingResponse: string, updateInitialState?: boolean) => void
    initialTopicDrawingData?: DrawingAnswer
}

const OpenCanvasButton = ({ question, questionType, saveLesson, stopDraggable, initialAssessmentDrawingData, updateDrawingResponses, initialTopicDrawingData, handleAnswerChange }: Props) => {
    const [openCanvas, setOpenCanvas] = useState<boolean>(false)
    const [loadingImage, setLoadingImage] = useState(true)

    const { setLessonDrawResponses } = useContext(AssignmentTopicContext)
    const { submissionId, reworkAssignmentMode } = useContext(AssignmentContext)
    const { isPartOfTopic, drawingResponses: lessonDrawingResponses, setDrawingResponses } = useContext(LessonContext)
    const { feedback } = useContext(LessonFeedbackContext)

    const [openImport, toggleImport] = useBoolean(false)
    const [drawingAnswers, setDrawingAnswers] = useState<DrawingAnswer[]>(null)

    const isLesson = isLessonQuestion(question, questionType)
    const isTopic = isTopicQuestion(question, questionType)
    const isAssessment = isAssessmentQuestion(question, questionType)

    const lessonQuestionFeedback = (isLesson && feedback) ? feedback.questionFeedbacks?.find(q => q.questionNumber === question.index && q.sectionNumber === question.sectionNumber) : null
    const lessonQuestionIsGraded = lessonQuestionFeedback?.grade != null && reworkAssignmentMode

    const existingLessonDrawing = (lessonDrawingResponses && isLesson) ? lessonDrawingResponses?.[`question-${question.sectionNumber}-${question.index}`] : null

    useEffect(() => {
        if (!submissionId || !openImport) {
            setDrawingAnswers(null)
            return
        }

        getSubmissionDrawingQuestions({ submissionId })
            .then((newDrawingAnswers) => {
                setDrawingAnswers(newDrawingAnswers ?? [])
            })
    }, [submissionId, openImport])

    // if there's no image for this question, resolve loadingImage immediately
    useEffect(() => {
        const hasImage = isLesson
            ? ((question.imageUpdated && question.imagePath) || existingLessonDrawing?.drawingResponse)
            : isTopic
            ? (initialTopicDrawingData?.drawingResponse || question.questionImageUrl)
            : false

        if (!hasImage) setLoadingImage(false)
    }, [isLesson, isTopic, question, existingLessonDrawing, initialTopicDrawingData])

    const onOpen = () => {
        setOpenCanvas(true)
        stopDraggable?.()
    }

    const onMinimize = useCallback(() => {
        setOpenCanvas(false)
        
        if (isLesson) {
            saveLesson({ force: true })
            stopDraggable()
        }
    }, [isLesson, saveLesson, stopDraggable])

    const copyDrawing = (answer: DrawingAnswer) => {
        toggleImport()
        if (isLesson) {
            if (isPartOfTopic) {
                setLessonDrawResponses((prev) => ({
                    ...prev,
                    [`question-${question.sectionNumber}-${question.index}`]: answer,
                }))
            } else {
                setDrawingResponses(prev => ({ ...prev, [`question-${question.sectionNumber}-${question.index}`]: answer }))
            }
        }

        if (isTopic) {
            handleAnswerChange(question.sectionNumber, question?.sortOrder, answer.answer, answer.drawingResponse, true)
        }

        if (isAssessment) {
            updateDrawingResponses({ questionNumber: question.number, answer: answer.answer, base64String: answer.drawingResponse, updateInitialState: true})
        }

        setOpenCanvas(true)
    }
    const questionId = isLesson ? `question-${question.sectionNumber}-${question.index}` : "" // used for highlighting incomplete question

  return (
    <div id={questionId}>
        {lessonQuestionIsGraded && <ToolTip title="graded"><i className="fas fa-circle-check text-success fs-3 me-1" /></ToolTip>}
        {isLesson && <div className="pe-2">{question.lessonQuestionIndex}. <CustomHTML html={question.content} /></div>}
        <i className="incompleteExclamation d-none fas fa-exclamation-circle text-warning position-absolute" style={{top: '8px', left: '0'}} />

        {/* if existing lesson drawing exists, use it as thumbnail rather than question image */}
        {isLesson &&
        <div>
            {loadingImage && <BeatLoader />}
            {((question.imageUpdated && question.imagePath) || existingLessonDrawing?.drawingResponse) && 
            <ToolTip title="Open Canvas">
                <img
                    role="button"
                    onClick={onOpen}
                    onLoad={() => setLoadingImage(false)}
                    onError={() => setLoadingImage(false)}
                    className={`${styles.lessonImage} img-fluid center-block p-1`} 
                    src={existingLessonDrawing?.drawingResponse ?? question.imagePath}
                    alt={question.imageAltText ?? 'Question image'}
                />
            </ToolTip>
            }
        </div>}

        {/* if existing topic drawing exists, use it as thumbnail rather than question image */}
        {isTopic && 
        <div>
            {loadingImage && <BeatLoader />}
            {(initialTopicDrawingData?.drawingResponse || question.questionImageUrl) && 
            <ToolTip title="Open Canvas">
                <Image 
                    fluid 
                    role="button"
                    className={`${styles.lessonImage} img-fluid center-block p-1`} 
                    onClick={onOpen}
                    onLoad={() => setLoadingImage(false)}
                    onError={() => setLoadingImage(false)}
                    src={initialTopicDrawingData?.drawingResponse ?? question.questionImageUrl} 
                    alt={'Topic Question image'} />
            </ToolTip>}
        </div>}

        <div className="d-flex justify-content-center my-4">
            <Button onClick={onOpen}>
                Open Canvas
            </Button>

            <Button className="mx-2" onClick={() => {
                toggleImport()
                stopDraggable?.()
            }}>
                Import Drawing
            </Button>
        </div>
        <TeacherFeedback grade={lessonQuestionFeedback?.grade} comment={lessonQuestionFeedback?.comment} show={reworkAssignmentMode && !!lessonQuestionFeedback} />
        {openCanvas && 
            <FullScreenWrapper fullScreen={openCanvas}>
                <DrawingQuestion 
                    questionType={questionType}
                    question={question}
                    minimizeCanvas={onMinimize}
                    initialTopicDrawingData={initialTopicDrawingData}
                    handleAnswerChange={handleAnswerChange}
                    initialAssessmentDrawingData={initialAssessmentDrawingData}
                    updateDrawingResponses={updateDrawingResponses}
                     />
            </FullScreenWrapper> 
        }

        <DrawingQuestionImport
            copyDrawing={copyDrawing}
            drawingAnswers={drawingAnswers}
            openImport={openImport}
            toggleImport={() => {
                toggleImport()
                stopDraggable?.()
            }}
        />

    </div>
    )
}

export default OpenCanvasButton
